import React from "react";

type IconProps = React.SVGProps<SVGSVGElement>;

/** Completed-step icon: filled brand circle with a white check (24px). */
export const StepCheckIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <circle cx="12" cy="12" r="12" fill="currentColor" />
    <path
      d="M7.5 12.2 10.6 15.3 16.5 9.4"
      stroke="#fff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Active radio: brand ring + dot (24px). */
export const RadioActiveIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="4" fill="currentColor" />
  </svg>
);

/** Inactive radio: neutral ring (24px). */
export const RadioIdleIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

/** Accordion chevron (points down when expanded). */
export const AccordionChevronIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M4 6.25 8 10.25 12 6.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Calendar glyph for the date cells (20px). */
export const CalendarIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.05 2.17c.46 0 .83.37.83.83h1.67c.92 0 1.66.75 1.66 1.67v11.66c0 .92-.74 1.67-1.66 1.67H4.88c-.92 0-1.67-.75-1.67-1.67V4.67c0-.92.75-1.67 1.67-1.67h1.67c0-.46.37-.83.83-.83.46 0 .83.37.83.83h5c0-.46.38-.83.84-.83ZM6.55 4.67c0 .46.37.83.83.83.46 0 .83-.37.83-.83h5c0 .46.38.83.84.83.46 0 .83-.37.83-.83h1.67v1.66H4.88V4.67h1.67ZM4.88 8v8.33h11.67V8H4.88Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Figma "16 / Collapse-icon" (node 722:89752) — brackets on the outer corners.
 * Sits in the compact card, where the button opens fullscreen. Draw at 16x16.
 */
export const CollapseIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M8 3.75732H12.2426V8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 12.2427H3.75736V8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Figma "16 / Expand-icon" (node 722:89873) — brackets folding towards the
 * centre. Sits in the fullscreen card, where the button returns to the compact
 * layout. Draw at 16x16.
 */
export const ExpandIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M13.2122 7.0303H8.9695V2.78763"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.80273 8.9546H7.0454V13.1972"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Superseded by the Figma geometry above — kept for reference. */
export const CollapseIcon_old: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M5.77 10.5H1V5.73"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.23 1.5H10V6.27"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Info icon of the Installs column (Figma 148:57537): filled circle with an
 * "i" cut out. Fill follows currentColor — idle gray, brand blue on hover.
 */
export const InfoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17316C0.00433284 8.00042 -0.1937 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8078C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C19.9949 7.34939 18.9397 4.80881 17.0655 2.93455C15.1912 1.06028 12.6506 0.00508267 10 0ZM9.80769 4.61538C10.0359 4.61538 10.259 4.68306 10.4487 4.80984C10.6385 4.93663 10.7864 5.11683 10.8737 5.32767C10.961 5.53851 10.9839 5.77051 10.9394 5.99433C10.8948 6.21816 10.785 6.42375 10.6236 6.58512C10.4622 6.74649 10.2566 6.85638 10.0328 6.9009C9.80897 6.94543 9.57697 6.92258 9.36614 6.83524C9.1553 6.74791 8.97509 6.60002 8.84831 6.41027C8.72152 6.22052 8.65385 5.99744 8.65385 5.76923C8.65385 5.46321 8.77541 5.16973 8.9918 4.95334C9.20819 4.73695 9.50168 4.61538 9.80769 4.61538ZM10.7692 15.3846H10C9.79599 15.3846 9.60033 15.3036 9.45607 15.1593C9.31182 15.0151 9.23077 14.8194 9.23077 14.6154V10C9.02676 10 8.8311 9.91895 8.68684 9.7747C8.54258 9.63044 8.46154 9.43478 8.46154 9.23077C8.46154 9.02675 8.54258 8.8311 8.68684 8.68684C8.8311 8.54258 9.02676 8.46154 9.23077 8.46154H10C10.204 8.46154 10.3997 8.54258 10.5439 8.68684C10.6882 8.8311 10.7692 9.02675 10.7692 9.23077V13.8462C10.9732 13.8462 11.1689 13.9272 11.3132 14.0715C11.4574 14.2157 11.5385 14.4114 11.5385 14.6154C11.5385 14.8194 11.4574 15.0151 11.3132 15.1593C11.1689 15.3036 10.9732 15.3846 10.7692 15.3846Z"
      fill="currentColor"
    />
  </svg>
);
